import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Modal, Alert, Form, Card, Table } from 'react-bootstrap';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import PageTitle from '@/common/PageTitle/PageTitle';
import LifecycleRulesDrawer, {
  LifecycleRulesInfoButton,
} from '@/common/LifecycleRulesDrawer/LifecycleRulesDrawer';
import { DEVICE_ASSIGNMENTS_LIFECYCLE_RULES } from '@/features/lifecycle-rules/lifecycleRulesContent';
import FormItem from '@/common/FormItem/FormItem';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import dayjs from 'dayjs';
import styles from './inventory-device-assignments.module.scss';
import {
  isDeviceFleetListEnabled,
} from '@/features/devices/devicePreview';
import {
  useGetDevicesQuery,
  useRegisterPhysicalDeviceMutation,
  useIssueFirstTrustMutation,
  useRevokeFirstTrustMutation,
} from '@/features/devices/devicesApi';
import {
  DeviceAssignmentRow,
  displayTrustOrInventoryStatus,
  mapFleetDeviceToAssignmentRow,
  statusIcon,
} from '@/features/devices/deviceAssignmentRows';
import {
  canOpenFirstTrust,
  canReEnrollFirstTrust,
  canRevokeFirstTrust,
  FIRST_TRUST_AWAITING_POLL_MS,
  firstTrustIcon,
  firstTrustLabel,
  firstTrustPhase,
  FirstTrustPurpose,
  isAwaitingFirstTrustPlayer,
  isCredentialBound,
  isFirstTrustOpenEligible,
  isFirstTrustPlayerComplete,
  partitionFirstTrustSelection,
  TRUST_LIFECYCLE_FILTER_OPTIONS,
  trustLifecycleLabel,
  trustLifecycleStatus,
} from '@/features/devices/firstTrust';
import FirstTrustModal, {
  FirstTrustOpenResult,
  ReEnrollConfirmPayload,
} from '@/features/devices/FirstTrustModal';
import BulkFirstTrustModal, {
  BulkFirstTrustResult,
} from '@/features/devices/BulkFirstTrustModal';

const isStagingUi = () =>
  process.env.REACT_APP_STAGE === 'staging' ||
  process.env.REACT_APP_ENV === 'staging' ||
  process.env.REACT_APP_ENV === 'development';

const DeviceAssignments: React.FC = () => {
  const fleetEnabled = isDeviceFleetListEnabled();

  const [registerPhysicalDevice, { isLoading: registering }] =
    useRegisterPhysicalDeviceMutation();
  const [issueFirstTrust, { isLoading: issuingFirstTrust }] =
    useIssueFirstTrustMutation();
  const [revokeFirstTrust, { isLoading: revokingFirstTrust }] =
    useRevokeFirstTrustMutation();

  const [filters, setFilters] = useState({
    master_accounts: null as string | null,
    zones: null as string | null,
    status: null as string | null,
    playTimeStart: null as any,
    playTimeEnd: null as any,
    serialQuery: '',
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceAssignmentRow | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerSerial, setRegisterSerial] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState<'success' | 'warning' | 'danger'>('success');
  const [resetCounter, setResetCounter] = useState(0);
  const [firstTrustDevice, setFirstTrustDevice] = useState<DeviceAssignmentRow | null>(null);
  const [firstTrustPurpose, setFirstTrustPurpose] =
    useState<FirstTrustPurpose>('first-bind');
  const [firstTrustAuthorized, setFirstTrustAuthorized] = useState<FirstTrustOpenResult | null>(null);
  const [firstTrustError, setFirstTrustError] = useState<string | null>(null);
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [showBulkFirstTrust, setShowBulkFirstTrust] = useState(false);
  const [bulkFirstTrustResult, setBulkFirstTrustResult] =
    useState<BulkFirstTrustResult | null>(null);
  const [bulkAuthorizing, setBulkAuthorizing] = useState(false);
  const [lifecycleHelpOpen, setLifecycleHelpOpen] = useState(false);
  const [batchAction, setBatchAction] = useState<'first-trust' | null>(null);
  const completedWindowRef = React.useRef<string | null>(null);

  // Bounded poll ONLY while First Trust modal is Awaiting Player (not page-wide).
  const awaitingFirstTrustPlayer = isAwaitingFirstTrustPlayer(
    firstTrustDevice,
    firstTrustAuthorized,
  );

  const {
    data: fleetData,
    isLoading: fleetLoading,
    isError: fleetError,
    refetch,
  } = useGetDevicesQuery(
    {},
    {
      skip: !fleetEnabled,
      pollingInterval: awaitingFirstTrustPlayer ? FIRST_TRUST_AWAITING_POLL_MS : 0,
    },
  );

  const liveRows: DeviceAssignmentRow[] = useMemo(() => {
    if (!fleetEnabled) return [];
    return (fleetData?.items || []).map(mapFleetDeviceToAssignmentRow);
  }, [fleetData, fleetEnabled]);

  const assignedDevices = liveRows;

  // Keep modal device row in sync with authoritative list while open.
  // Do not clobber an optimistic open window with a stale list row that still
  // shows the previous consumed window id.
  useEffect(() => {
    if (!firstTrustDevice?.serialNumber) return;
    const live = liveRows.find(
      (d) => d.serialNumber === firstTrustDevice.serialNumber,
    );
    if (!live) return;
    if (
      firstTrustAuthorized?.firstTrustWindowId &&
      live.firstTrustWindowId &&
      live.firstTrustWindowId !== firstTrustAuthorized.firstTrustWindowId &&
      (live.firstTrustStatus || '').toLowerCase() !== 'open'
    ) {
      return;
    }
    if (
      live.id === firstTrustDevice.id &&
      live.firstTrustStatus === firstTrustDevice.firstTrustStatus &&
      live.firstTrustWindowId === firstTrustDevice.firstTrustWindowId &&
      live.pendingTrustKind === firstTrustDevice.pendingTrustKind &&
      live.enrollmentStatus === firstTrustDevice.enrollmentStatus &&
      live.credentialBound === firstTrustDevice.credentialBound &&
      live.assignedTo === firstTrustDevice.assignedTo &&
      live.location === firstTrustDevice.location &&
      live.zone === firstTrustDevice.zone &&
      live.firstTrustExpiresAt === firstTrustDevice.firstTrustExpiresAt
    ) {
      return;
    }
    setFirstTrustDevice(live);
  }, [liveRows, firstTrustDevice, firstTrustAuthorized]);

  // On Player completion: list refetch via poll already ran; toast once.
  useEffect(() => {
    if (!firstTrustAuthorized || !firstTrustDevice) return;
    if (!isFirstTrustPlayerComplete(firstTrustDevice, firstTrustAuthorized)) {
      return;
    }
    const wid = firstTrustAuthorized.firstTrustWindowId || '';
    if (!wid || completedWindowRef.current === wid) return;
    completedWindowRef.current = wid;
    const purposeLabel =
      firstTrustPurpose === 're-enroll' ||
      firstTrustAuthorized.purpose === 're-enroll'
        ? 'Re-enrollment'
        : 'Enrollment';
    setAlertVariant('success');
    setAlertMessage(
      `${purposeLabel} complete for serial ${firstTrustDevice.serialNumber}.`,
    );
    setShowSuccessAlert(true);
  }, [firstTrustDevice, firstTrustAuthorized, firstTrustPurpose]);

  const inventoryStatusOptions = [
    ...new Set(assignedDevices.map((d) => d.inventoryStatus || d.status).filter(Boolean)),
  ].map((status) => ({
    value: String(status).toLowerCase(),
    label: String(status),
  }));
  const filterOptions = {
    masterAccounts: [...new Set(assignedDevices.map((d) => d.assignedTo).filter(Boolean))].map(
      (account) => ({
        value: account.toLowerCase().replace(/\s+/g, '-'),
        label: account,
      })
    ),
    zones: [...new Set(assignedDevices.map((d) => d.zone).filter(Boolean))].map((zone) => ({
      value: zone.toLowerCase().replace(/\s+/g, '-'),
      label: zone,
    })),
    statuses: [
      ...TRUST_LIFECYCLE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      ...inventoryStatusOptions.filter(
        (o) =>
          !TRUST_LIFECYCLE_FILTER_OPTIONS.some((t) => t.value === o.value) &&
          o.value !== 'enrollment-required' &&
          o.value !== 're-enrollment-required' &&
          o.value !== 'enrolled'
      ),
    ],
    serials: [...new Set(assignedDevices.map((d) => d.serialNumber).filter(Boolean))].map(
      (serial) => ({
        value: serial,
        label: serial,
      })
    ),
  };

  const selectInputs = [
    {
      label: 'Master Accounts',
      name: 'master_accounts',
      placeholder: 'Any master account',
      options: filterOptions.masterAccounts,
    },
    {
      label: 'Zones',
      name: 'zones',
      placeholder: 'Any zone',
      options: filterOptions.zones,
    },
    {
      label: 'Status',
      name: 'status',
      placeholder: 'Any status',
      options: filterOptions.statuses,
    },
  ];

  const filteredDevices = assignedDevices.filter((device) => {
    const matchesMasterAccount =
      !filters.master_accounts ||
      device.assignedTo.toLowerCase().replace(/\s+/g, '-') === filters.master_accounts;
    const matchesZone =
      !filters.zones || device.zone.toLowerCase().replace(/\s+/g, '-') === filters.zones;
    const lifecycle = trustLifecycleStatus(device);
    const matchesStatus =
      !filters.status ||
      lifecycle === filters.status ||
      (device.inventoryStatus || device.status || '').toLowerCase() === filters.status;
    const q = filters.serialQuery.trim().toLowerCase();
    const matchesSerial =
      !q || device.serialNumber.toLowerCase().includes(q);
    return matchesMasterAccount && matchesZone && matchesStatus && matchesSerial;
  });

  const selectedDevices = filteredDevices.filter((d) =>
    selectedSerials.includes(d.serialNumber)
  );
  const { eligible: bulkEligible, ineligible: bulkIneligible } =
    partitionFirstTrustSelection(selectedDevices);
  const eligibleOnPage = filteredDevices.filter((d) => isFirstTrustOpenEligible(d));
  const allEligibleSelected =
    eligibleOnPage.length > 0 &&
    eligibleOnPage.every((d) => selectedSerials.includes(d.serialNumber));

  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        playTimeStart: dates[0],
        playTimeEnd: dates[1],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        playTimeStart: null,
        playTimeEnd: null,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      master_accounts: null,
      zones: null,
      status: null,
      playTimeStart: null,
      playTimeEnd: null,
      serialQuery: '',
    });
    setResetCounter((c) => c + 1);
    setBatchAction(null);
  };

  const handleEditDevice = (_device: DeviceAssignmentRow) => {
    setAlertVariant('warning');
    setAlertMessage(
      'Pre-shipment assignment editing is not enabled in this slice. Physical inventory registration is available.'
    );
    setShowSuccessAlert(true);
  };

  const handleUnassignDevice = (_deviceId: string) => {
    setAlertVariant('warning');
    setAlertMessage('Unassign is not enabled in this physical-inventory slice.');
    setShowSuccessAlert(true);
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    const serial = registerSerial.trim();
    if (!serial) {
      setRegisterError('Serial Number is required');
      return;
    }
    try {
      const result = await registerPhysicalDevice({ serialNumber: serial }).unwrap();
      setShowRegisterModal(false);
      setRegisterSerial('');
      setAlertVariant('success');
      setAlertMessage(
        `Registered physical Player serial ${result.serialNumber} (${result.inventoryStatus}).`
      );
      setShowSuccessAlert(true);
      refetch();
    } catch (err: any) {
      const message =
        err?.data?.message ||
        err?.error ||
        (typeof err?.message === 'string' ? err.message : null) ||
        'Could not register serial';
      setRegisterError(message);
    }
  };

  const openFirstTrust = (
    device: DeviceAssignmentRow,
    purpose: FirstTrustPurpose = 'first-bind',
  ) => {
    setFirstTrustError(null);
    setFirstTrustAuthorized(null);
    setFirstTrustPurpose(purpose);
    setFirstTrustDevice(device);
  };

  const closeFirstTrust = () => {
    setFirstTrustDevice(null);
    setFirstTrustAuthorized(null);
    setFirstTrustError(null);
    setFirstTrustPurpose('first-bind');
    completedWindowRef.current = null;
  };

  const handleConfirmFirstTrust = async (payload?: ReEnrollConfirmPayload) => {
    if (!firstTrustDevice?.serialNumber) return;
    if (firstTrustPurpose === 're-enroll') {
      if (!canReEnrollFirstTrust(firstTrustDevice)) {
        setFirstTrustError(
          'This Player is not eligible for First Trust re-enrollment.',
        );
        return;
      }
      if (!payload?.assignmentIntent) {
        setFirstTrustError(
          'Select whether to keep or change the current assignment.',
        );
        return;
      }
      if (
        payload.assignmentIntent === 'CHANGE_ASSIGNMENT' &&
        !(payload.masterAccountId && payload.locationId && payload.zoneId)
      ) {
        setFirstTrustError(
          'Select Master Account, Location, and Zone for the new assignment.',
        );
        return;
      }
    } else if (!canOpenFirstTrust(firstTrustDevice)) {
      setFirstTrustError('This Player is not eligible for First Trust authorization.');
      return;
    }
    setFirstTrustError(null);
    try {
      const result = await issueFirstTrust({
        serialNumber: firstTrustDevice.serialNumber,
        ...(firstTrustPurpose === 're-enroll'
          ? {
              purpose: 're-enroll',
              assignmentIntent: payload!.assignmentIntent,
              ...(payload!.assignmentIntent === 'CHANGE_ASSIGNMENT'
                ? {
                    masterAccountId: payload!.masterAccountId,
                    locationId: payload!.locationId,
                    zoneId: payload!.zoneId,
                  }
                : {}),
            }
          : {}),
      }).unwrap();
      setFirstTrustAuthorized({
        serialNumber: result.serialNumber,
        expiresAt: result.expiresAt,
        firstTrustStatus: result.firstTrustStatus,
        firstTrustWindowId: result.firstTrustWindowId,
        environment: result.environment || result.stage,
        purpose: result.purpose || firstTrustPurpose,
        assignmentIntent: result.assignmentIntent || payload?.assignmentIntent || null,
      });
      // Optimistic open so Awaiting Player poll starts before list catch-up.
      setFirstTrustDevice((prev) =>
        prev && prev.serialNumber === result.serialNumber
          ? {
              ...prev,
              firstTrustStatus: result.firstTrustStatus || 'open',
              firstTrustExpiresAt: result.expiresAt || prev.firstTrustExpiresAt,
              firstTrustWindowId:
                result.firstTrustWindowId || prev.firstTrustWindowId,
              firstTrustPurpose:
                result.purpose || firstTrustPurpose || prev.firstTrustPurpose,
              pendingTrustKind: '',
              pendingTrustDeviceId: '',
            }
          : prev,
      );
      refetch();
    } catch (err: any) {
      setFirstTrustError(
        err?.data?.message ||
          err?.error ||
          (typeof err?.message === 'string' ? err.message : null) ||
          'Could not issue First Trust'
      );
    }
  };

  const handleRevokeFirstTrust = async () => {
    if (!firstTrustDevice?.serialNumber) return;
    setFirstTrustError(null);
    try {
      await revokeFirstTrust({
        serialNumber: firstTrustDevice.serialNumber,
        reason: 'sa-first-trust-revoke',
      }).unwrap();
      setFirstTrustAuthorized(null);
      setAlertVariant('success');
      setAlertMessage(
        `Revoked First Trust for serial ${firstTrustDevice.serialNumber}.`
      );
      setShowSuccessAlert(true);
      closeFirstTrust();
      refetch();
    } catch (err: any) {
      setFirstTrustError(
        err?.data?.message ||
          err?.error ||
          (typeof err?.message === 'string' ? err.message : null) ||
          'Could not revoke First Trust'
      );
    }
  };

  const toggleSelected = (serial: string) => {
    if (!serial) return;
    setSelectedSerials((prev) =>
      prev.includes(serial) ? prev.filter((s) => s !== serial) : [...prev, serial]
    );
  };

  const toggleSelectEligibleOnPage = () => {
    if (allEligibleSelected) {
      const eligibleSet = new Set(eligibleOnPage.map((d) => d.serialNumber));
      setSelectedSerials((prev) => prev.filter((s) => !eligibleSet.has(s)));
      return;
    }
    setSelectedSerials((prev) => {
      const next = new Set(prev);
      eligibleOnPage.forEach((d) => next.add(d.serialNumber));
      return Array.from(next);
    });
  };

  const openBulkFirstTrust = () => {
    setBulkFirstTrustResult(null);
    setShowBulkFirstTrust(true);
  };

  const handleBatchAction = (value: 'first-trust' | null) => {
    setBatchAction(null);
    if (value === 'first-trust') {
      openBulkFirstTrust();
    }
  };

  const closeBulkFirstTrust = () => {
    setShowBulkFirstTrust(false);
    setBulkFirstTrustResult(null);
    setBulkAuthorizing(false);
  };

  const handleBulkFirstTrustConfirm = async () => {
    if (bulkEligible.length === 0) return;
    setBulkAuthorizing(true);
    const succeeded: BulkFirstTrustResult['succeeded'] = [];
    const failed: BulkFirstTrustResult['failed'] = [];
    for (const device of bulkEligible) {
      try {
        const result = await issueFirstTrust({
          serialNumber: device.serialNumber,
        }).unwrap();
        succeeded.push({
          serialNumber: result.serialNumber,
          expiresAt: result.expiresAt,
          firstTrustWindowId: result.firstTrustWindowId,
        });
      } catch (err: any) {
        failed.push({
          serialNumber: device.serialNumber,
          message:
            err?.data?.message ||
            err?.error ||
            (typeof err?.message === 'string' ? err.message : null) ||
            'Could not authorize First Trust',
        });
      }
    }
    setBulkFirstTrustResult({ succeeded, failed });
    setBulkAuthorizing(false);
    refetch();
  };

  return (
    <Container fluid>
      <Form>
        <Card>
          <Card.Body>
            <Card.Title
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <PageTitle title="Device Assignments" />
                <LifecycleRulesInfoButton onClick={() => setLifecycleHelpOpen(true)} />
              </span>
              <Button
                onClick={handleResetFilters}
                style={{
                  background: 'white',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500',
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>

            {!fleetEnabled && (
              <div
                className="alert alert-secondary"
                role="status"
                data-testid="device-assignments-disconnected"
              >
                <strong>Device assignments not connected yet</strong>
                <div>Device assignments are not connected to Device Fleet V2 yet.</div>
              </div>
            )}

            {showSuccessAlert && (
              <Alert
                variant={alertVariant}
                onClose={() => setShowSuccessAlert(false)}
                dismissible
                className="mb-3"
              >
                {alertMessage}
              </Alert>
            )}

            {fleetError && (
              <Alert variant="danger" className="mb-3">
                Could not load Device Fleet devices.
              </Alert>
            )}

            <Row className="my-2">
              <Col>
                <Row>
                  {selectInputs.map((input) => (
                    <Col key={input.name} md={4}>
                      <FormItem
                        name={input.name}
                        label={input.label}
                        className="customSelectWrapper"
                      >
                        <Select
                          key={`${input.name}-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className="customSelect"
                          placeholder={input.placeholder}
                          value={filters[input.name as keyof typeof filters] as string | null}
                          onChange={(value) =>
                            setFilters((prev) => ({ ...prev, [input.name]: value }))
                          }
                          options={input.options}
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                        />
                      </FormItem>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
            <Row className="my-2">
              <Col md={4}>
                <FormItem
                  name="dateRange"
                  validateTrigger="onFinish"
                  label="Filter by Date Range"
                  className="customSelectWrapper"
                >
                  <DateRangePicker
                    name="dateRange"
                    maxDate={dayjs()}
                    onChange={handleDateChange}
                    style={{ width: '100%' }}
                  />
                </FormItem>
              </Col>
              <Col md={4}>
                <FormItem
                  name="serialQuery"
                  label="Serial Number"
                  className="customSelectWrapper"
                >
                  <Select
                    key={`serial-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="Search serial…"
                    value={filters.serialQuery || null}
                    onChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        serialQuery: (value as string) || '',
                      }))
                    }
                    onSearch={(value) =>
                      setFilters((prev) => ({ ...prev, serialQuery: value }))
                    }
                    options={filterOptions.serials}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label || '')
                        .toLowerCase()
                        .includes(String(input).toLowerCase())
                    }
                    style={{ width: '100%' }}
                    data-testid="serial-search"
                  />
                </FormItem>
              </Col>
            </Row>
            {fleetEnabled && isStagingUi() && (
              <Row className="mb-2">
                <Col md={4}>
                  <FormItem
                    label="Batch Actions"
                    className="customSelectWrapper"
                    layout="vertical"
                  >
                    <Select<'first-trust'>
                      className="customSelect"
                      placeholder="Batch Actions"
                      value={batchAction}
                      onChange={handleBatchAction}
                      disabled={selectedSerials.length === 0}
                      options={[{ label: 'First Trust', value: 'first-trust' }]}
                      data-testid="device-batch-actions"
                      style={{ width: '100%' }}
                    />
                  </FormItem>
                </Col>
                <Col md={4} />
                <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
                  <Button
                    type="button"
                    className="w-100"
                    data-testid="register-physical-player"
                    style={{
                      background: 'white',
                      color: '#e21e26',
                      border: '1px solid #e21e26',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      justifyContent: 'center',
                      height: '50px',
                      marginTop: '8px',
                    }}
                    onClick={() => {
                      setRegisterError(null);
                      setShowRegisterModal(true);
                    }}
                  >
                    <PlusOutlined style={{ fontSize: 16 }} />
                    Register Player
                  </Button>
                </Col>
              </Row>
            )}
            <Row className="mt-4">
              <Col>
                <Table hover responsive className={`legacy-row-height-table devices-table`}>
                  <thead>
                    <tr>
                      {fleetEnabled && isStagingUi() && (
                        <th style={{ width: 36 }}>
                          <Form.Check
                            type="checkbox"
                            aria-label="Select eligible Players on this page"
                            checked={allEligibleSelected}
                            onChange={toggleSelectEligibleOnPage}
                            data-testid="first-trust-select-all-eligible"
                          />
                        </th>
                      )}
                      <th>Product</th>
                      <th>Serial Number</th>
                      <th>Custom ID</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>First Trust</th>
                      <th>Assigned To</th>
                      <th>Location</th>
                      <th>Zone</th>
                      <th style={{ whiteSpace: 'nowrap', maxWidth: 120 }}>Last Seen</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fleetLoading ? (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-4">
                          Loading Device Fleet…
                        </td>
                      </tr>
                    ) : filteredDevices.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-4">
                          No physical devices found
                        </td>
                      </tr>
                    ) : (
                      filteredDevices.map((device) => (
                        <tr key={device.id} data-testid={`device-row-${device.serialNumber}`}>
                          {fleetEnabled && isStagingUi() && (
                            <td>
                              <Form.Check
                                type="checkbox"
                                aria-label={`Select ${device.serialNumber}`}
                                checked={selectedSerials.includes(device.serialNumber)}
                                onChange={() => toggleSelected(device.serialNumber)}
                                data-testid={`first-trust-select-${device.serialNumber}`}
                              />
                            </td>
                          )}
                          <td className={styles.productName}>{device.productName}</td>
                          <td className={styles.serialNumber}>
                            <strong>{device.serialNumber || '—'}</strong>
                          </td>
                          <td className={styles.customId}>{device.customId || '—'}</td>
                          <td className={styles.iconCell}>
                            {(() => {
                              const lifecycle = trustLifecycleStatus(device);
                              const label =
                                trustLifecycleLabel(lifecycle) ||
                                displayTrustOrInventoryStatus(device);
                              const st = statusIcon(lifecycle || device.status);
                              return (
                                <span
                                  className={styles.statusIcon}
                                  title={label}
                                  aria-label={label}
                                  data-testid={`device-status-${device.serialNumber}`}
                                >
                                  <i className={st.icon} aria-hidden="true" />
                                  <span className="visually-hidden">{label}</span>
                                  <span
                                    className={styles.statusText}
                                    data-testid={`device-status-label-${device.serialNumber}`}
                                  >
                                    {label}
                                  </span>
                                </span>
                              );
                            })()}
                          </td>
                          <td className={styles.iconCell}>
                            {(() => {
                              const phase = firstTrustPhase(device);
                              const ft = firstTrustIcon(phase);
                              const lifecycle = trustLifecycleStatus(device);
                              return (
                                <span
                                  className={styles.statusIcon}
                                  title={ft.title}
                                  aria-label={ft.title}
                                  data-testid={`first-trust-status-${device.serialNumber}`}
                                >
                                  <i className={ft.icon} aria-hidden="true" />
                                  <span className="visually-hidden">
                                    {trustLifecycleLabel(lifecycle) ||
                                      firstTrustLabel(phase)}
                                  </span>
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <div className={styles.clientInfo}>
                              <div className={styles.clientName}>
                                {device.assignedTo || <span className="text-muted">—</span>}
                              </div>
                              {(device.contactEmail || device.contactPhone) && (
                                <div className={styles.clientDetails}>
                                  <small>{device.contactEmail}</small>
                                  <br />
                                  <small>{device.contactPhone}</small>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{device.location || '—'}</td>
                          <td>{device.zone || '—'}</td>
                          <td className={styles.lastSeen}>
                            {device.lastSeen || <span className="text-muted">Never</span>}
                          </td>
                          <td className={styles.actionsCell}>
                            <div className={styles.actionButtons}>
                            {fleetEnabled &&
                              isStagingUi() &&
                              canOpenFirstTrust(device) && (
                                <Button
                                  variant="link"
                                  type="button"
                                  className={styles.actionLink}
                                  title="Enroll Player"
                                  aria-label={`Enroll Player ${device.serialNumber}`}
                                  onClick={() => openFirstTrust(device, 'first-bind')}
                                  data-testid={`first-trust-${device.serialNumber}`}
                                >
                                  <i className="bi bi-shield-lock" aria-hidden="true" />
                                  <span className="visually-hidden">Enroll Player</span>
                                </Button>
                              )}
                            {fleetEnabled &&
                              isStagingUi() &&
                              canReEnrollFirstTrust(device) && (
                                <Button
                                  variant="link"
                                  type="button"
                                  className={styles.actionLink}
                                  title="Re-enroll Player"
                                  aria-label={`Re-enroll Player ${device.serialNumber}`}
                                  onClick={() => openFirstTrust(device, 're-enroll')}
                                  data-testid={`first-trust-reenroll-${device.serialNumber}`}
                                >
                                  <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                  <span className="visually-hidden">Re-enroll Player</span>
                                </Button>
                              )}
                            {fleetEnabled &&
                              isStagingUi() &&
                              canRevokeFirstTrust(device) && (
                                <Button
                                  variant="link"
                                  type="button"
                                  className={styles.actionLink}
                                  title="Revoke Authorization"
                                  aria-label={`Revoke authorization for ${device.serialNumber}`}
                                  onClick={() =>
                                    openFirstTrust(
                                      device,
                                      isCredentialBound(device)
                                        ? 're-enroll'
                                        : 'first-bind',
                                    )
                                  }
                                  data-testid={`first-trust-manage-${device.serialNumber}`}
                                >
                                  <i className="bi bi-x-octagon" aria-hidden="true" />
                                </Button>
                              )}
                            <Button
                              variant="link"
                              type="button"
                              className={styles.actionLink}
                              title="Edit assignment"
                              aria-label={`Edit assignment for ${device.serialNumber}`}
                              onClick={() => handleEditDevice(device)}
                            >
                              <i className="bi bi-pencil" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="link"
                              type="button"
                              className={styles.actionLink}
                              title="Unassign"
                              aria-label={`Unassign ${device.serialNumber}`}
                              onClick={() => handleUnassignDevice(device.id)}
                            >
                              <i className="bi bi-trash" aria-hidden="true" />
                            </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>

      <Modal
        show={showRegisterModal}
        onHide={() => setShowRegisterModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Register KrossKast Player</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRegisterSubmit}>
          <Modal.Body>
            <p className="text-muted">
              Register the manufacturer serial before the Player is powered on.
              This creates physical inventory only — it does not enroll a
              cryptographic identity.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>
                Serial Number <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={registerSerial}
                onChange={(e) => setRegisterSerial(e.target.value)}
                placeholder="e.g. 2603270220001"
                autoFocus
                data-testid="register-serial-input"
              />
            </Form.Group>
            {registerError && <Alert variant="danger">{registerError}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowRegisterModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={registering}>
              {registering ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <BulkFirstTrustModal
        show={showBulkFirstTrust}
        selected={selectedDevices}
        eligible={bulkEligible}
        ineligible={bulkIneligible}
        authorizing={bulkAuthorizing}
        result={bulkFirstTrustResult}
        onClose={closeBulkFirstTrust}
        onConfirm={handleBulkFirstTrustConfirm}
      />

      <FirstTrustModal
        show={!!firstTrustDevice}
        device={firstTrustDevice}
        stageLabel="staging"
        opening={issuingFirstTrust}
        revoking={revokingFirstTrust}
        error={firstTrustError}
        authorized={firstTrustAuthorized}
        purpose={firstTrustPurpose}
        onClose={closeFirstTrust}
        onConfirmOpen={handleConfirmFirstTrust}
        onRevoke={handleRevokeFirstTrust}
      />

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Device Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingDevice && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Serial Number</Form.Label>
                    <Form.Control type="text" value={editingDevice.serialNumber} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Product</Form.Label>
                    <Form.Control type="text" value={editingDevice.productName} readOnly />
                  </Form.Group>
                </Col>
              </Row>
              <Alert variant="secondary">
                Pre-shipment assignment writes remain a later gate.
              </Alert>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <LifecycleRulesDrawer
        content={DEVICE_ASSIGNMENTS_LIFECYCLE_RULES}
        open={lifecycleHelpOpen}
        onClose={() => setLifecycleHelpOpen(false)}
      />
    </Container>
  );
};

export default DeviceAssignments;
